/* eslint-disable react/no-array-index-key */
import {
  Add,
  ArrowDropDown,
  CalendarMonth,
  CalendarViewWeek,
  EditLocation,
  Event,
  LocalOffer,
  NextPlan,
  NotificationsActive,
  Settings,
  Task,
} from '@mui/icons-material';
import {
  Alert,
  AppBar,
  Box,
  Button,
  ButtonGroup,
  Container,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  useColorScheme,
  useTheme,
} from '@mui/material/styles';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import AddEditBillableModalComponent from '../../../../pages/billing/components/AddEditBillableModal';
import AddEditModal from '../../../../pages/horse/Horse/components/NotesCard/components/AddEditModal';
import { setSelectedLocation } from '../../../../redux/location/actions';
import {
  formatDateOnly,
  getTimeZoneOffset,
} from '../../../../utils/dateHelper';
import { getQuestriPrimaryButtonSx } from '../../../../utils/questriButtonStyles';
import theme from '../../../../utils/theme';
import LocationAutocomplete from '../../../Autocompletes/Locations';
import BarnIcon from '../../../CustomIcons/Barn';

const PlannerAppBarComponent = ({
  selectedLocation,
  handleRefresh,
  isFetchingBoard,
  navigate,
  scheduleClick,
  weekStyle,
  setWeekStyle,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  filterComponent,
}) => {
  const dispatch = useDispatch();
  const muiTheme = useTheme();
  const { mode } = useColorScheme();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openCalendarSettings, setOpenCalendarSettings] = React.useState(null);
  const [openAddEditBillableModal, setOpenAddEditBillableModal] = React.useState(false);
  const [openAddEditHorseNoteModal, setOpenAddEditHorseNoteeModal] = React.useState(false);
  const [openAddButton, setOpenAddButton] = React.useState(false);
  const [anchorElAddButton, setAnchorElAddButton] = React.useState(null);

  const mdDown = useMediaQuery(theme.breakpoints.down('md'))
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));

  // const barnUsersMap = useSelector((state) => state.barn.barnUsersMap);
  const permissions = useSelector((state) => state.user.permissions);
  const realTimeUpdateError = useSelector((state) => state.board.realTimeUpdateError);
  const barnHorses = useSelector((state) => state.board.barnHorses);
  const errorMessage = useSelector((state) => state.board.errorMessage);
  const isFetchingEventTemplates = useSelector((state) => state.eventTemplate.isFetchingEventTemplates);

  // const numberOfDueDates = Object.keys(boardSuggestions).map((key) => boardSuggestions[key].dueDates || []).filter((dd) => dd.length > 0).length;

  const palette = muiTheme.colorSchemes?.[mode]?.palette || muiTheme.palette;
  const appBarBackground = palette.background.paper;
  const appBarTextColor = palette.text.primary;

  const handleChangeLocation = (newLocation) => {
    if (newLocation && newLocation.id) {
      if (newLocation.timezone !== (selectedLocation && selectedLocation.timezone)) {
        // reset dates for timezone
        const timezoneOffsetMillis = getTimeZoneOffset(newLocation.timezone);
        setStartDate(new Date(startDate.getTime() + timezoneOffsetMillis))
        setEndDate(new Date(endDate.getTime() + timezoneOffsetMillis))
      }

      global.window.localStorage.setItem('barn_board_location', JSON.stringify(newLocation));
      global.window.localStorage.setItem('barn_board_location_location_board', null);
      dispatch(setSelectedLocation(newLocation));
    }
  };

  return (
    <AppBar
      position="relative"
      color="inherit"
      sx={{
        backgroundColor: appBarBackground,
        color: appBarTextColor,
        borderBottom: `1px solid ${palette.divider}`,
      }}
      elevation={2}
    >
      <Container maxWidth="100%" sx={{ px: '16px !important' }}>
        <Toolbar disableGutters>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <LocationAutocomplete
              disabled={isFetchingBoard}
              multiple={false}
              value={(selectedLocation && selectedLocation) || null}
              onSelect={(e, newLocations) => {
                handleChangeLocation(newLocations)
              }}
              limitTags={1}
              size="small"
              icon
              v2
            />
            {
              mobile
                ? (
                  <IconButton
                    disabled={isFetchingBoard}
                    id="calendar-customized-button"
                    aria-controls={openAddButton
                      ? 'calendar-customized-menu'
                      : undefined}
                    aria-haspopup="true"
                    color="primary"
                    aria-expanded={openAddButton
                      ? 'true'
                      : undefined}
                    variant="contained"
                    disableElevation
                    onClick={(e) => {
                      setOpenAddButton(!openAddButton);
                      setAnchorElAddButton(e.currentTarget);
                    }}
                    size="large"
                  >
                    <Add />
                  </IconButton>
                )
                : (
                  <ButtonGroup
                    variant="outlined"
                    disableElevation
                    color="inherit"
                    size="large"
                    sx={{
                      minWidth: 200,
                      overflow: 'visible',
                      display: 'flex',
                      '& .MuiButtonGroup-grouped': {
                        textTransform: 'none',
                        fontWeight: 600,
                        minHeight: 40,
                        lineHeight: 1,
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  >
                    <Button
                      disabled={isFetchingBoard}
                      sx={{
                        flex: 1,
                        minWidth: 155,
                        justifyContent: 'center',
                        px: 2,
                        gap: 1,
                        ...getQuestriPrimaryButtonSx(),
                      }}
                      onClick={() => {
                        scheduleClick(false, false, false);
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Add sx={{ fontSize: 20 }} />
                        <span>Create</span>
                      </Box>
                    </Button>
                    <Button
                      id="calendar-customized-button"
                      aria-controls={openAddButton
                        ? 'calendar-customized-menu'
                        : undefined}
                      aria-haspopup="true"
                      aria-expanded={openAddButton
                        ? 'true'
                        : undefined}
                      onClick={(e) => {
                        setOpenAddButton(!openAddButton);
                        setAnchorElAddButton(e.currentTarget);
                      }}
                      sx={{
                        px: 1,
                        minWidth: 36,
                        ...getQuestriPrimaryButtonSx({
                          isSplitRight: true,
                          isOpen: openAddButton,
                        }),
                      }}
                    >
                      <ArrowDropDown />
                    </Button>
                  </ButtonGroup>
                )
            }
            <Menu
              id="calendar-customized-menu"
              slotProps={{ list: { 'aria-labelledby': 'calendar-customized-button' } }}
              anchorEl={anchorElAddButton}
              open={openAddButton}
              onClose={() => {
                setOpenAddButton(false);
                setAnchorElAddButton(null);
              }}
              sx={{
                '& .MuiPaper-root': {
                  backgroundColor: palette.mode === 'dark'
                    ? palette.grey[900]
                    : palette.background.paper,
                  color: palette.mode === 'dark'
                    ? palette.grey[50]
                    : palette.text.primary,
                  border: `1px solid ${palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.12)'
                    : 'rgba(0, 0, 0, 0.12)'}`,
                },
                '& .MuiMenuItem-root:hover': {
                  backgroundColor: palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  setOpenAddButton(false);
                  setAnchorElAddButton(null);
                  scheduleClick(false, false, true);
                }}
                disableRipple
              >
                <Task sx={{ mr: 1 }} />
                Horse Task
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setOpenAddButton(false);
                  setAnchorElAddButton(null);
                  scheduleClick(false, true, false);
                }}
                disableRipple
              >
                <Box sx={{ mr: 1 }}><BarnIcon /></Box>
                Barn Task
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setOpenAddButton(false);
                  setAnchorElAddButton(null);
                  scheduleClick(true, false, false);
                }}
                disableRipple
              >
                <Event sx={{ mr: 1 }} />
                Event
              </MenuItem>
            </Menu>
          </Box>
          {
            !mdDown
              ? (
                <Box
                  sx={{
                    flex: 1,
                    textAlign: 'center',
                    lineHeight: 1,
                  }}
                >
                  <Button
                    id="preview-board-tomorrow"
                    size="large"
                    disableElevation
                    startIcon={<CalendarMonth />}
                    variant="outlined"
                  >
                    {moment(startDate).isSame(endDate, 'day')
                      ? `${formatDateOnly(new Date(new Date().getTime() + 86400000))}`
                      : moment(startDate).format('ll')} - {moment(endDate).format('ll')}
                  </Button>
                  {!!realTimeUpdateError && (
                    <Alert
                      severity="error"
                      sx={{
                        m: 1,
                        width: 'fit-content',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                      }}
                      action={(
                        <Button onClick={handleRefresh} color="inherit" size="small" sx={{ color: 'white' }}>
                          Refresh Now
                        </Button>
                      )}
                    >
                      {realTimeUpdateError || 'There was an error with the board connection - data will be wrong'}
                    </Alert>
                  )}

                  {
                    (!isFetchingBoard && !isFetchingEventTemplates && !errorMessage
                      && selectedLocation && selectedLocation.id && barnHorses.length === 0)
                    && (
                      <Alert
                        severity="info"
                        sx={{
                          m: 1,
                          width: 'fit-content',
                          marginLeft: 'auto',
                          marginRight: 'auto',
                        }}
                        action={(
                          <Button onClick={() => navigate(`/settings/locations?locationId=${selectedLocation.id}`)} color="inherit" size="small">
                            Manage Horses
                          </Button>
                        )}
                      >
                        No horses at this location for the time range
                      </Alert>
                    )
                  }
                </Box>
              )
              : (<Box sx={{ flex: 1 }} />)
          }
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >

            {/* {(numberOfDueDates && navigate)
              && (
                <Tooltip title={`You have ${numberOfDueDates} horses with reminders at this location. Click to view all reminder information.`}>
                  <IconButton size="small" onClick={() => { navigate('/tasks/reminders'); }}><NotificationsActive color="error" /></IconButton>
                </Tooltip>
              )} */}
            {
              filterComponent && (
                filterComponent
              )
            }
            <Tooltip title="Calendar Settings">
              <IconButton aria-label="manage calendar settings" onClick={(e) => { setAnchorEl(e.currentTarget); setOpenCalendarSettings(true); }}>
                <Settings />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </Container>
      <Menu
        id="calendar-settings-menu"
        anchorEl={anchorEl}
        open={openCalendarSettings || false}
        onClose={() => { setAnchorEl(null); setOpenCalendarSettings(false); }}
      >
        {(permissions.admin || permissions.owner) && (
          <Box>
            <ListSubheader>Calendar Settings</ListSubheader>
            <MenuItem onClick={() => { setOpenCalendarSettings(false); setAnchorEl(null); navigate('/settings/calendar') }}>
              <ListItemIcon>
                <CalendarMonth />
              </ListItemIcon>
              <ListItemText
                primary="Manage Calendars"
              />
            </MenuItem>
            <Divider />
            <ListSubheader>Task Settings</ListSubheader>
            <MenuItem onClick={() => { setOpenCalendarSettings(false); setAnchorEl(null); navigate('/settings/tasks') }}>
              <ListItemIcon>
                <Task />
              </ListItemIcon>
              <ListItemText
                primary="Horse and Barn Tasks"
              />
            </MenuItem>
            <MenuItem onClick={() => { setOpenCalendarSettings(false); setAnchorEl(null); navigate('/settings/tasks/reminders') }}>
              <ListItemIcon>
                <NotificationsActive />
              </ListItemIcon>
              <ListItemText
                primary="Reminders"
              />
            </MenuItem>
            <MenuItem onClick={() => { setOpenCalendarSettings(false); setAnchorEl(null); navigate('/settings/tasks/statuses') }}>
              <ListItemIcon>
                <NextPlan />
              </ListItemIcon>
              <ListItemText
                primary="Statuses"
              />
            </MenuItem>
            <MenuItem onClick={() => { setOpenCalendarSettings(false); setAnchorEl(null); navigate('/settings/tasks/calendar') }}>
              <ListItemIcon>
                <LocalOffer />
              </ListItemIcon>
              <ListItemText
                primary="Tags"
              />
            </MenuItem>
            <Divider />
            <ListSubheader>Other Settings</ListSubheader>
          </Box>
        )}
        <MenuItem onClick={() => { setOpenCalendarSettings(false); setAnchorEl(null); navigate(`/settings/locations?locationId=${selectedLocation.id}`) }}>
          <ListItemIcon>
            <EditLocation />
          </ListItemIcon>
          <ListItemText
            primary="Manage Location"
            secondary="Manage horses at this location, assigned planning user, and planning reminders."
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenCalendarSettings(false); setWeekStyle(weekStyle === 'isoWeek'
              ? 'week'
              : 'isoWeek'); setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <CalendarViewWeek />
          </ListItemIcon>
          <ListItemText
            primary={weekStyle === 'isoWeek'
              ? 'Switch to Standard Week (Sunday-Saturday)'
              : 'Switch to ISO Week (Monday-Sunday)'}
            secondary="Change the week style for your calendar view."
          />
        </MenuItem>
      </Menu>

      <AddEditBillableModalComponent
        open={openAddEditBillableModal}
        billable={null}
        onClose={() => { setOpenAddEditBillableModal(false); }}
        navigate={navigate}
      />
      <AddEditModal
        open={openAddEditHorseNoteModal}
        horseNote={null}
        presetType={null}
        horseId={null}
        pinnedHorseNoteId={null}
        onClose={() => setOpenAddEditHorseNoteeModal(false)}
      />
    </AppBar>
  )
}

PlannerAppBarComponent.propTypes = {
  selectedLocation: PropTypes.object.isRequired,
  navigate: PropTypes.any.isRequired,
  scheduleClick: PropTypes.func.isRequired,
  handleRefresh: PropTypes.func.isRequired,
  weekStyle: PropTypes.string.isRequired,
  setWeekStyle: PropTypes.func.isRequired,
  isFetchingBoard: PropTypes.bool.isRequired,
  startDate: PropTypes.any.isRequired,
  endDate: PropTypes.any.isRequired,
  setStartDate: PropTypes.func.isRequired,
  setEndDate: PropTypes.func.isRequired,
  filterComponent: PropTypes.any,
};

PlannerAppBarComponent.defaultProps = { filterComponent: null };

export default (PlannerAppBarComponent);
